import { didApiUrl } from '@/environment';
import { DRAFT_TEXT_PLACEHOLDER } from '@/hooks/constants';
import { mapVoices } from '@/services/api/voices';
import { authenticatedAxios } from '@/services/auth';
import { S3Creds } from '@/utils/s3';
import { ApiVoicesResponse, FaceRect, IClipDriver, IVoice, LibraryItemType, Scene, SceneAvatar } from '@d-id/types';
import { CreateApiKeyResponse } from '@d-id/types/src/entities/ApiKey';
import axios from 'axios';
import { logger } from '../../logger';
import { uploadFile } from '../studioApi';

export type ChatSessions = { freeLeft: number; total: number; expire_at: string; isExpired: boolean };

export const getOrganization = async (orgId: string): Promise<{ name: string }> => {
    const response = await authenticatedAxios.get(`${didApiUrl}/organizations/${orgId}`);
    return response.data;
};

export const deleteItemFromLibrary = async ({ videoId, type }: { videoId: string; type: LibraryItemType }) => {
    // check empty string
    if (!videoId) {
        return;
    }

    const endPoint = `${type}s`;
    const response = await authenticatedAxios.delete(`${didApiUrl}/${endPoint}/${videoId}`);

    return response.data;
};

export const getVideoDurationInSeconds = async ({
    video,
    handleUploadFile,
    signal,
}: {
    video: File;
    handleUploadFile: (file: File) => Promise<string>;
    signal?: AbortSignal;
}): Promise<{ duration: number }> => {
    const sourceUrl = await handleUploadFile(video);

    const response = await authenticatedAxios?.post(`${didApiUrl}/images/video-duration`, { source_url: sourceUrl }, { signal });
    return response?.data;
};

export const applyBackground = async ({
    source_url,
    background_url,
    bucket,
    image_id,
}: {
    source_url: string;
    background_url: string;
    bucket: string;
    image_id?: string;
}): Promise<{ result_url: string }> => {
    const response = await authenticatedAxios.post(`${didApiUrl}/images/apply-background`, {
        source_url,
        background_url,
        bucket,
        image_id,
    });

    return response.data;
};

export const directUpload = async ({
    file,
    metadata,
}: {
    file: File;
    metadata?: Record<string, unknown>;
}): Promise<{ url: string; id?: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('metadata', JSON.stringify(metadata));
    const response = await authenticatedAxios.post(`${didApiUrl}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const updateVideo = async (id: string, updateData: Record<string, any>, type: LibraryItemType) => {
    const response = await authenticatedAxios.patch(`${didApiUrl}/${type}s/${id}`, updateData);
    if (!response.data) throw new Error();
    return response.data;
};

export const uploadAudio = async ({
    audio,
    sourceUrl,
    resultUrl,
    signal,
    persist,
}: {
    audio?: File;
    sourceUrl?: string;
    resultUrl?: string;
    signal?: AbortSignal;
    persist?: boolean;
}) => {
    if (sourceUrl) {
        return uploadAudioFromSourceUrl({ sourceUrl, resultUrl, signal, persist });
    } else if (audio) {
        try {
            return uploadAudioToPresignedUrl({ audio, resultUrl, signal, persist });
        } catch (err) {
            return uploadAudioFromBuffer({ audio, resultUrl, signal });
        }
    }

    throw new Error('No audio source provided');
};

const uploadAudioToPresignedUrl = async ({
    audio,
    resultUrl,
    signal,
    persist,
}: {
    audio: File;
    resultUrl?: string;
    signal?: AbortSignal;
    persist?: boolean;
}) => {
    try {
        const sourceUrl = await uploadFile(audio, signal);
        const response = await uploadAudioFromSourceUrl({ sourceUrl, resultUrl, signal, persist });
        return response;
    } catch (err) {
        logger.error('failed to upload audio to presigned url', { err, errJson: JSON.stringify(err) });
        throw err;
    }
};

const uploadAudioFromSourceUrl = async ({
    sourceUrl,
    resultUrl,
    signal,
    persist,
}: {
    sourceUrl: string;
    resultUrl?: string;
    signal?: AbortSignal;
    persist?: boolean;
}) => {
    try {
        const audiosFormData = new FormData();
        audiosFormData.append('source_url', sourceUrl);
        if (persist) {
            audiosFormData.append('persist', 'true');
        }

        if (resultUrl) {
            audiosFormData.append('result_url', resultUrl);
        }
        const audiosResponse = await authenticatedAxios.post(`${didApiUrl}/audios`, audiosFormData, {
            headers: { 'Content-Type': `multipart/form-data` },
            signal,
        });
        return { ...audiosResponse.data, sourceFile: sourceUrl };
    } catch (err) {
        logger.error('failed to upload audio to presigned url', { err, errJson: JSON.stringify(err) });
        throw err;
    }
};

const uploadAudioFromBuffer = async ({ audio, resultUrl, signal }: { audio: File; resultUrl?: string; signal?: AbortSignal }) => {
    try {
        const audiosFormData = new FormData();
        audiosFormData.append('audio', audio);

        if (resultUrl) {
            audiosFormData.append('result_url', resultUrl);
        }
        const audiosResponse = await authenticatedAxios.post(`${didApiUrl}/audios`, audiosFormData, {
            headers: { 'Content-Type': `multipart/form-data` },
            signal,
        });
        return audiosResponse.data;
    } catch (err) {
        logger.error('failed to upload audio', { err, errJson: JSON.stringify(err) });
    }
};

export const createTtsAudio = async (payload: any) => {
    const response = await authenticatedAxios.post(`${didApiUrl}/audios/tts`, payload);
    return response.data;
};
export const getProjectScenes = async (id: string): Promise<Scene[]> => {
    const response = await authenticatedAxios?.get(`${didApiUrl}/projects/${id}/scenes`);

    if (response?.data) {
        return response.data?.map((scene: any) => {
            const presenterScript = {
                ...(scene?.presenter?.script ? scene.presenter.script : {}),
                input: (scene?.presenter?.script?.input || '').replace(DRAFT_TEXT_PLACEHOLDER, ''),
            };
            const sceneScript = {
                ...(scene?.script ? scene.script : {}),
                input: (scene?.script?.input || '').replace(DRAFT_TEXT_PLACEHOLDER, ''),
            };

            return {
                ...scene,
                script: { ...presenterScript, ...sceneScript },
                ...(scene?.presenter
                    ? {
                          presenter: {
                              ...scene.presenter,
                              script: {},
                          },
                      }
                    : {}),
            };
        });
    }

    return response?.data;
};

export const getApiKeyMetadata = async (): Promise<{ modified_at: Date | string; is_trial?: boolean } | undefined> => {
    try {
        const response = await authenticatedAxios.get(`${didApiUrl}/users/api-keys`);
        const { data } = response;

        return typeof data === 'string' ? { modified_at: data } : data;
    } catch (e) {}
};

export const generateApiKey = async (): Promise<CreateApiKeyResponse> => {
    const response = await authenticatedAxios?.post<CreateApiKeyResponse>(`${didApiUrl}/users/api-keys`);
    return response?.data;
};

export const getUploadUrl = async (id: string) => {
    const response = await authenticatedAxios?.get<{ raw: S3Creds; thumbnail: S3Creds }>(`${didApiUrl}/projects/uploadurl/${id}`);
    return response?.data;
};

export const validateElevenlabsKey = async (key: string) => {
    return axios.get<IVoice[]>(`https://api.elevenlabs.io/v1/voices`, { headers: { ['xi-api-key']: key } });
};

export const getAllVoices = async () => {
    const response = await authenticatedAxios?.get<ApiVoicesResponse>(`${didApiUrl}/tts/voices`);
    return mapVoices(response?.data);
};

export const getVoicesSeats = async () => {
    const response = await authenticatedAxios?.get<{ seats: number }>(`${didApiUrl}/tts/voices/seats`);
    return response?.data.seats || 0;
};

export const getClipPresenterDrivers = (presenterId: string) => async (): Promise<IClipDriver[]> => {
    const response = await authenticatedAxios?.get(`${didApiUrl}/clips/presenters/${presenterId}/drivers`);
    return response?.data.clips_drivers as IClipDriver[];
};

export const updateClipPresenterName = async (presenterId: string, new_name: string) => {
    const response = await authenticatedAxios?.patch(`${didApiUrl}/clips/presenters/${presenterId}`, { new_name });
    return response?.data;
};

export const uploadDocument = async (file: File) => {
    const sourceUrl = await uploadFile(file);
    return sourceUrl;
};

export const validateExpressAvatarUserCapping = async (): Promise<boolean> => {
    const response = await authenticatedAxios.get<{ isValid: boolean }>(`${didApiUrl}/scenes/avatars/validate-capping`);
    return response.data.isValid;
};

export const getExpressAvatarUserCapping = async (): Promise<{ isValid: boolean; remaining?: number; unlimited?: boolean }> => {
    const response = await authenticatedAxios.get<{ isValid: boolean; remaining?: number; unlimited?: boolean }>(
        `${didApiUrl}/scenes/avatars/validate-capping`
    );
    return response.data;
};

export const validatePremiumAvatarUserCapping = async (): Promise<boolean> => {
    const response = await authenticatedAxios.get<{ isValid: boolean }>(`${didApiUrl}/clips/train/validate-capping`);
    return response.data.isValid;
};

export const cloneVoice = async (data: { file: string; name: string; language?: string }): Promise<void> => {
    const formData = new FormData();
    formData.append('source_url', data.file);

    formData.append('name', data.name);
    if (data.language) {
        formData.append('language', data.language);
    }

    const response = await authenticatedAxios?.post(`${didApiUrl}/tts/voices/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response?.data;
};

export const deleteVoice = async (id: string): Promise<void> => {
    const response = await authenticatedAxios?.delete(`${didApiUrl}/tts/voices/${id}`);

    return response?.data;
};

export const getFaceCrops = async ({
    sourceUrl,
    shouldSkipDetection,
}: {
    sourceUrl: string;
    shouldSkipDetection?: boolean;
}): Promise<{ faceCrops: FaceRect[]; metadata: { width: number; height: number; length: number } }> => {
    const response = await authenticatedAxios?.post(`${didApiUrl}/images/face-detection`, {
        source_url: sourceUrl,
        should_skip_detection: shouldSkipDetection,
    });
    return response?.data;
};

type UpdateSceneAvatarPayload = Pick<SceneAvatar, 'name'>;

export const updateSceneAvatar = async (avatarId: string, payload: UpdateSceneAvatarPayload) => {
    const response = await authenticatedAxios.patch(`${didApiUrl}/scenes/avatars/${avatarId}`, payload);
    return response.data;
};

export const updateA2eModel = async (presenterId: string, payload: { a2e_model: string }) => {
    await authenticatedAxios.patch(`${didApiUrl}/clips/presenters/${presenterId}`, payload);
};

export interface CreateChatExportRequest {
    agent_id: string;
    from?: string;
    to?: string;
    token?: string;
}

export interface ChatExportStatus {
    status: 'created' | 'started' | 'done' | 'partial' | 'error';
    result?: {
        token?: string;
        result_url: string;
    };
    error?: string;
}

export const createChatExport = async (payload: CreateChatExportRequest): Promise<{ export_id: string }> => {
    const response = await authenticatedAxios.post(`${didApiUrl}/agents/chats/exports`, payload);
    return response.data;
};

export const getChatExportStatus = async (exportId: string): Promise<ChatExportStatus> => {
    const response = await authenticatedAxios.get(`${didApiUrl}/agents/chats/exports/${exportId}`);
    return response.data;
};
